import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import { SegmentControl } from '@thoughtspot/radiant-react/widgets/segment-control';
import { SegmentedControlItem } from '@thoughtspot/radiant-react/widgets/segment-control/segment-control-item';
import { isSpreadsheetsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { trackSpreadsheetToggleClick } from '../../components/answer-spreadsheet/spreadsheet-mixpanel-util';
import { getComputedNavThemeType } from '../../components/nav/navigation/left-navigation.util';
import { LeftNavigationOverlayHub } from '../../components/nav/navigation/left-navigation-overlay-hub.container';
import styles from './search-data-layout.module.scss';

enum ViewMode {
    Query = 'query',
    Spreadsheet = 'spreadsheet',
}

interface SearchDataHeaderProps {
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
}

const SearchDataHeader: React.FC<SearchDataHeaderProps> = React.memo(
    ({ viewMode, onViewModeChange }) => {
        const [
            showLeftNavigationOverlayHub,
            setShowLeftNavigationOverlayHub,
        ] = React.useState(false);

        const {
            customStylingService,
            sessionService,
            metricsService,
        } = useAppContext();
        const themeType = useMemo(
            () =>
                getComputedNavThemeType(customStylingService.getConfig(), true),
            [customStylingService],
        );

        const hamburgerIconHoverHandlerDebounce = useMemo(
            () =>
                _.debounce((value: boolean) => {
                    if (value) return;
                    const el = document.querySelector('.popover-module__in');
                    if (el) return;
                    setShowLeftNavigationOverlayHub(value);
                }, 100),
            [],
        );

        const hamburgerIconHoverHandler = useCallback(
            (
                value: boolean,
                fromHamburgerIcon = false,
                forceToClose = false,
            ) => {
                if (fromHamburgerIcon) {
                    (document.activeElement as HTMLElement | null)?.blur?.();
                    document.dispatchEvent(new MouseEvent('mousedown'));
                    setShowLeftNavigationOverlayHub(true);
                    return;
                }
                if (forceToClose) {
                    setShowLeftNavigationOverlayHub(false);
                    return;
                }
                hamburgerIconHoverHandlerDebounce(value);
            },
            [hamburgerIconHoverHandlerDebounce],
        );

        const handleHamburgerMouseOver = useCallback(() => {
            if (!sessionService.isHambugerHidden()) {
                hamburgerIconHoverHandler(true, true);
            }
        }, [sessionService, hamburgerIconHoverHandler]);

        // Mirrors TopNavV3: leaving the header schedules a debounced close;
        // if the cursor lands inside the overlay hub, its mouseenter fires
        // handler(true) which resets the debounce and keeps the hub open.
        const handleHeaderMouseEnter = useCallback(() => {
            hamburgerIconHoverHandler(true);
        }, [hamburgerIconHoverHandler]);

        const handleHeaderMouseLeave = useCallback(() => {
            hamburgerIconHoverHandler(false);
        }, [hamburgerIconHoverHandler]);

        const handleHamburgerClick = useCallback(() => {
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.HAMBURGER_ICON_CLICK),
                {},
            );
        }, [metricsService]);

        const selectedIndex = viewMode === ViewMode.Query ? 0 : 1;

        // SegmentControl fires onSelect more than once per user action: the
        // item's click handler fires it, then flipping the item's internal
        // isSelected state fires it again via a useEffect, and the initially
        // selected item fires it on mount. Track the last-handled index —
        // seeded with the current selection so mount fires are absorbed — so
        // navigation and the toggle metric run exactly once per real toggle.
        const lastHandledIndexRef = useRef(selectedIndex);

        const handleViewSelect = useCallback(
            (index: number) => {
                if (index === lastHandledIndexRef.current) {
                    return;
                }
                lastHandledIndexRef.current = index;
                if (index === 1) {
                    trackSpreadsheetToggleClick(metricsService);
                }
                onViewModeChange(
                    index === 0 ? ViewMode.Query : ViewMode.Spreadsheet,
                );
            },
            [onViewModeChange, metricsService],
        );

        return (
            <>
                <div
                    className={styles.header}
                    onMouseEnter={handleHeaderMouseEnter}
                    onMouseLeave={handleHeaderMouseLeave}
                >
                    <button
                        type="button"
                        data-testid="hamburger-icon"
                        className={styles.hamburgerIconContainer}
                        onMouseOver={handleHamburgerMouseOver}
                        onFocus={() => null} // No-op fn to disable lints, on focus is not provided to match with existing functionality
                        onClick={handleHamburgerClick}
                    >
                        <Icon
                            className={styles.hamburgerIcon}
                            id={IconID.HAMBURGER}
                            color={IconColor.BLACK}
                            size={IconSize.LARGE}
                        />
                    </button>
                    <h1 className={styles.title}>Search data</h1>
                    <div className={styles.toggle}>
                        <SegmentControl
                            selectedIndex={selectedIndex}
                            onSelect={handleViewSelect}
                        >
                            <SegmentedControlItem
                                title="Query"
                                aria-selected={viewMode === ViewMode.Query}
                                isSelected={viewMode === ViewMode.Query}
                            />
                            <SegmentedControlItem
                                title="Spreadsheet"
                                aria-selected={
                                    viewMode === ViewMode.Spreadsheet
                                }
                                isSelected={viewMode === ViewMode.Spreadsheet}
                            />
                        </SegmentControl>
                    </div>
                </div>
                <LeftNavigationOverlayHub
                    hamburgerIconHoverHandler={hamburgerIconHoverHandler}
                    showLeftNavigationOverlayHub={showLeftNavigationOverlayHub}
                    themeType={themeType}
                />
            </>
        );
    },
);

export const SearchDataLayout: React.FC = () => {
    const location = useLocation();
    const { navService } = useAppContext();
    const spreadsheetsEnabled = isSpreadsheetsEnabled();

    const isSpreadsheet = location.pathname.endsWith('/spreadsheet');
    const viewMode: ViewMode = isSpreadsheet
        ? ViewMode.Spreadsheet
        : ViewMode.Query;

    const handleViewModeChange = useCallback(
        (mode: ViewMode) => {
            if (mode === ViewMode.Spreadsheet && !isSpreadsheet) {
                navService.goToPath(`${location.pathname}/spreadsheet`);
            } else if (mode === ViewMode.Query && isSpreadsheet) {
                navService.goToPath(
                    location.pathname.replace(/\/spreadsheet$/, ''),
                );
            }
        },
        [navService, location.pathname, isSpreadsheet],
    );

    if (spreadsheetsEnabled && !isEmbeddedApp()) {
        return (
            <div className={styles.searchDataLayout}>
                <SearchDataHeader
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                />
                <div className={styles.queryContent}>
                    <Outlet />
                </div>
            </div>
        );
    }
    return <Outlet />;
};
